import { useEffect } from "react";
import { TestCase, EvalResult, Criterion } from "../../services/evals";

interface UseUrlStateProps {
  searchParams: URLSearchParams;
  currentTab: string;
  evalResults: EvalResult[] | undefined;
  testCases: TestCase[] | undefined;
  criteria: Criterion[] | undefined;
  showCreateModal: boolean;
  setSelectedResult: (result: EvalResult | null) => void;
  setExpandedResultId: (id: string | null) => void;
  setSelectedTestCase: (testCase: TestCase | null) => void;
  setExpandedTestCaseId: (id: string | null) => void;
  setExpandedCriterionId: (id: string | null) => void;
  setExpandedBatchRun: (id: string | null) => void;
  setExpandedBatchRunResultId: (id: string | null) => void;
}

export const useUrlState = ({
  searchParams,
  currentTab,
  evalResults,
  testCases,
  criteria,
  showCreateModal,
  setSelectedResult,
  setExpandedResultId,
  setSelectedTestCase,
  setExpandedTestCaseId,
  setExpandedCriterionId,
  setExpandedBatchRun,
  setExpandedBatchRunResultId,
}: UseUrlStateProps) => {
  useEffect(() => {
    const resultId = searchParams.get("result_id");
    const testCaseId = searchParams.get("test_case_id");
    const testCaseExpandedId = searchParams.get("test_case_expanded_id");
    const criterionId = searchParams.get("criterion_id");
    const batchRun = searchParams.get("batch_run");
    const batchRunResultId = searchParams.get("batch_run_result_id");

    // Handle result_id parameter
    if (resultId && Array.isArray(evalResults)) {
      const result = evalResults.find(r => r.id === resultId);
      if (result) {
        // If on runs tab, open modal
        if (currentTab === "runs") {
          setSelectedResult(result);
        }
        // If on results tab, expand inline
        else if (currentTab === "results") {
          setExpandedResultId(resultId);
        }
      }
    } else {
      // Clear expanded result if no result_id in URL
      if (currentTab === "results") {
        setExpandedResultId(null);
      }
    }

    // Handle test_case_id parameter (modal)
    if (testCaseId && Array.isArray(testCases) && !showCreateModal) {
      const testCase = testCases.find(tc => tc.id === testCaseId);
      if (testCase) {
        setSelectedTestCase(testCase);
      }
    }

    // Handle test_case_expanded_id parameter (inline expansion)
    if (currentTab === "test-cases") {
      if (testCaseExpandedId && Array.isArray(testCases)) {
        const testCase = testCases.find(tc => tc.id === testCaseExpandedId);
        if (testCase) {
          setExpandedTestCaseId(testCaseExpandedId);
        }
      } else {
        setExpandedTestCaseId(null);
      }
    }

    // Handle criterion_id parameter
    if (currentTab === "criteria") {
      if (criterionId && Array.isArray(criteria)) {
        const criterion = criteria.find(c => c.id === criterionId);
        if (criterion) {
          setExpandedCriterionId(criterionId);
        }
      } else {
        setExpandedCriterionId(null);
      }
    }

    // Handle batch_run and batch_run_result_id parameters
    if (currentTab === "runs") {
      if (batchRun) {
        setExpandedBatchRun(batchRun);
      } else {
        setExpandedBatchRun(null);
      }

      if (batchRunResultId) {
        setExpandedBatchRunResultId(batchRunResultId);
      } else {
        setExpandedBatchRunResultId(null);
      }
    }
  }, [
    searchParams,
    evalResults,
    testCases,
    criteria,
    showCreateModal,
    currentTab,
    setSelectedResult,
    setExpandedResultId,
    setSelectedTestCase,
    setExpandedTestCaseId,
    setExpandedCriterionId,
    setExpandedBatchRun,
    setExpandedBatchRunResultId,
  ]);
};
